<script setup>
import { computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

// The AI message action stores its config as a single object inside the
// action_params array: [{ assistant_id, prompt, message }].
const props = defineProps({
  modelValue: {
    type: [Array, Object],
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue', 'input']);

const { t } = useI18n();
const store = useStore();

const assistants = useMapGetter('captainAssistants/getRecords');

const config = computed(() => {
  const value = Array.isArray(props.modelValue)
    ? props.modelValue[0]
    : props.modelValue;
  return value && typeof value === 'object'
    ? value
    : { assistant_id: null, prompt: '', message: '' };
});

const updateConfig = patch => {
  const next = [{ ...config.value, ...patch }];
  emit('update:modelValue', next);
  emit('input', next);
};

const assistantId = computed({
  get: () => config.value.assistant_id || null,
  set: value => updateConfig({ assistant_id: value }),
});

const prompt = computed({
  get: () => config.value.prompt || '',
  set: value => updateConfig({ prompt: value }),
});

const message = computed({
  get: () => config.value.message || '',
  set: value => updateConfig({ message: value }),
});

onMounted(() => {
  if (!assistants.value || assistants.value.length === 0) {
    store.dispatch('captainAssistants/get');
  }
});
</script>

<template>
  <div class="flex flex-col gap-3 flex-1">
    <label class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.AI_MESSAGE.ASSISTANT_LABEL') }}
      </span>
      <select
        v-model="assistantId"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      >
        <option :value="null" disabled>
          {{ t('AUTOMATION.AI_MESSAGE.ASSISTANT_PLACEHOLDER') }}
        </option>
        <option
          v-for="assistant in assistants"
          :key="assistant.id"
          :value="assistant.id"
        >
          {{ assistant.name }}
        </option>
      </select>
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.AI_MESSAGE.PROMPT_LABEL') }}
      </span>
      <textarea
        v-model="prompt"
        rows="2"
        :placeholder="t('AUTOMATION.AI_MESSAGE.PROMPT_PLACEHOLDER')"
        class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 resize-none"
      />
    </label>

    <label class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('AUTOMATION.AI_MESSAGE.MESSAGE_LABEL') }}
      </span>
      <WootMessageEditor
        v-model="message"
        rows="4"
        enable-variables
        :placeholder="t('AUTOMATION.AI_MESSAGE.MESSAGE_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
    </label>
  </div>
</template>
